/**
 * One file on a phone and an iPad: its picture, where it is used and what can be done, as a sheet (phone and iPad
 * upright) or as the detail pane (iPad on its side). Move to and Delete are steps inside the same place, as drawn.
 */
import { useState } from "react";
import type { LibraryAsset, LibraryFolder } from "../../shared/types";
import { GroupedList, ListRow } from "../components/season/GroupedList";
import { Button } from "../components/ui/Button";
import { Icon } from "../components/ui/Icon";
import { Sheet } from "../components/ui/Sheet";
import { agoLabel, bytesLabel, downloadHref, folderPath, kindName, lenLabel, trashLine, usedWords } from "./library";
import { useOpenDirect } from "../talk/handoff";
import { Media, useRowOf } from "./parts";

type Step = "main" | "move" | "delete";

export interface FileProps {
  a: LibraryAsset;
  folders: LibraryFolder[];
  trash: boolean;
  /** An own file the viewer may change: Move and Delete. */
  own: boolean;
  /** A talk the viewer can start a room from. */
  talk: boolean;
  making: boolean;
  onMakeRoom: () => void;
  onMove: (to: string | null) => void;
  onDelete: () => void;
  onRestore: () => void;
  onGo: (href: string) => void;
}

function Use({ u, onGo }: { u: LibraryAsset["uses"][number]; onGo: (href: string) => void }) {
  const { icon, what, href } = useRowOf(u);
  const openDirect = useOpenDirect();
  const go = u.kind === "chat" ? () => void openDirect(u.ref).catch(() => onGo(href)) : () => onGo(href);
  return <ListRow icon={icon} title={u.label} sub={what} onClick={href ? go : undefined} />;
}

function usedTitle(a: LibraryAsset): string {
  if (a.source !== "room") return "Where it’s used";
  return a.webinar_id ? "Where it plays" : "Where it’s from";
}

function parts(p: FileProps, step: Step, to: (s: Step) => void): { title: string; body: React.ReactNode; foot: React.ReactNode } {
  const { a, folders } = p;
  const back = (label: string) => <Button onClick={() => to("main")}>{label}</Button>;
  if (step === "move") {
    const places = folders.filter((f) => !f.system && f.id !== a.folder_id);
    return {
      title: "Move to",
      body: (
        <GroupedList>
          {a.folder_id ? <ListRow icon="allf" title="Assets, in no folder" onClick={() => p.onMove(null)} /> : null}
          {places.map((f) => (
            <ListRow key={f.id} icon="fold" title={folderPath(folders, f.id).map((x) => x.name).join(" › ")} onClick={() => p.onMove(f.id)} />
          ))}
        </GroupedList>
      ),
      foot: back("Back"),
    };
  }
  if (step === "delete") {
    return {
      title: "Delete",
      body: (
        <div className="sa-conf" role="alert">
          <h2>Delete <bdi>{a.name}</bdi>?</h2>
          <p>It stays in Trash for 30 days.</p>
          {a.uses.length ? <p>{usedWords(a.uses, "it")}</p> : null}
        </div>
      ),
      foot: (
        <>
          <Button variant="p" className="sa-bad" icon="trash" onClick={p.onDelete}>
            Delete
          </Button>
          {back("Cancel")}
        </>
      ),
    };
  }
  const meta = p.trash
    ? [trashLine(a, folders)]
    : [kindName(a), ...(a.duration_sec != null ? [lenLabel(a.duration_sec)] : []), ...(a.size_bytes ? [bytesLabel(a.size_bytes)] : []), agoLabel(a.created_at)];
  const href = downloadHref(a);
  const room = a.source === "room" && a.webinar_id;
  return {
    title: kindName(a),
    body: (
      <>
        <div className="sa-pv">
          <span className="sa-m">
            <Media a={a} />
            {a.duration_sec != null ? <span className="sa-dur">{lenLabel(a.duration_sec)}</span> : null}
          </span>
        </div>
        <h2 className="sa-fn">{a.name}</h2>
        <p className="k-meta sa-fm">
          <span className="k-dots">
            {meta.map((m) => (
              <span key={m}>{m}</span>
            ))}
          </span>
        </p>
        <h3 className="k-gh sa-uh">{usedTitle(a)}</h3>
        {a.uses.length ? (
          <GroupedList>
            {a.uses.map((u, i) => (
              <Use key={`${u.kind}-${u.ref}-${i}`} u={u} onGo={p.onGo} />
            ))}
          </GroupedList>
        ) : (
          <p className="sa-none">Not used yet</p>
        )}
        {p.own && !p.trash ? (
          <ul className="k-act sa-acts">
            <li>
              <button type="button" onClick={() => to("move")}>
                <Icon name="fmove" />
                Move
              </button>
            </li>
            <li>
              <button type="button" className="k-bad" onClick={() => to("delete")}>
                <Icon name="trash" />
                Delete
              </button>
            </li>
          </ul>
        ) : null}
      </>
    ),
    foot: p.trash ? (
      <Button variant="p" icon="refresh" onClick={p.onRestore}>
        Restore
      </Button>
    ) : (
      <>
        {p.talk ? (
          <Button variant="p" icon="plus" disabled={p.making} onClick={p.onMakeRoom}>
            Make a room from this
          </Button>
        ) : null}
        {room ? (
          <Button variant="p" icon="play" onClick={() => p.onGo(`/rooms/${a.webinar_id}`)}>
            Open its room
          </Button>
        ) : null}
        {href && a.ready ? (
          <a className="btn" href={href} download={a.name}>
            <Icon name="download" />
            Download
          </a>
        ) : null}
      </>
    ),
  };
}

/** The file as a sheet; it starts on its main step each time it opens. */
export function FileSheet({ p, onClose }: { p: FileProps; onClose: () => void }) {
  const [step, setStep] = useState<Step>("main");
  const v = parts(p, step, setStep);
  return (
    <Sheet open onClose={onClose} title={v.title} label={p.a.name} footer={v.foot ? <div className="sa-foot">{v.foot}</div> : undefined}>
      {v.body}
    </Sheet>
  );
}

/** The file as the detail pane's content, on the iPad on its side. */
export function FilePane({ p }: { p: FileProps }) {
  const [step, setStep] = useState<Step>("main");
  const v = parts(p, step, setStep);
  return (
    <div className="sa-pane">
      {step === "main" ? null : <h3 className="sa-pt">{v.title}</h3>}
      {v.body}
      <div className="sa-foot">{v.foot}</div>
    </div>
  );
}
